/**
 * Record factories and defaults (W1-03 core-model-ops; DESIGN.md §6.6, §6.10, ARCHITECTURE.md §5.1).
 *
 * New projects: one BPM point at beat 0, package settings as Phira's importer would set them (aspect 16/9, dim 0.6,
 * line length 6, `useAttachUiFix: true`), Classic (legacy) speed with RPEVersion 160, the RPE-compatible export
 * profile and a 0.5 px speed-bake tolerance. Ids come from the per-document counter: the empty document's tempo
 * point gets id 1 and `idSeq` starts at 2.
 */

import { Beats } from '../time/beat';
import type {
  BpmId,
  BpmPoint,
  ChartDoc,
  ChartMeta,
  ControlCurve,
  LineEditorData,
  LineKind,
  LineProp,
  LineRpeData,
  ModelDefaultsApi,
  NoteControls,
  NoteTemplate,
  PackageSettings,
  ProjectEditorData,
  RpeRootData,
  Timing,
} from './types';

/** First id handed out in a new document. */
export const FIRST_ID = 1;
/** Seconds of the song-select preview Phira plays (previewEnd = previewStart + 15 when not set). */
export const PREVIEW_LENGTH_SEC = 15;
/** RPE's sentinel for "always visible" notes (`visibleTime: null` in the model). */
export const RPE_VISIBLE_ALWAYS = 999999.0;
/** x of the last point of RPE's default 2-point note control. */
export const RPE_DEFAULT_CONTROL_END_X = 9999999.0;

function meta(): ChartMeta {
  return {
    name: '',
    composer: '',
    illustrator: '',
    charter: '',
    level: '',
    difficulty: 0,
    previewStart: 0,
    previewEnd: null,
    tags: [],
    intro: '',
    tip: null,
    music: null,
    illustration: null,
    unlockVideo: null,
    rpeId: '',
    metaExtra: [],
  };
}

function pkg(): PackageSettings {
  return {
    aspectRatio: 16 / 9,
    forceAspectRatio: false,
    backgroundDim: 0.6,
    lineLength: 6,
    holdPartialCover: false,
    noteUniformScale: false,
    useAttachUiFix: true,
    speedMode: 'legacy',
    rpeVersion: 160,
    rpeVersionRaw: null,
    infoOffsetSec: 0,
    exportProfile: 'rpe-compatible',
    fileNaming: 'readable',
    speedBakeTolPx: 0.5,
    infoExtra: [],
    server: null,
    extraJsonRoot: null,
  };
}

function timing(): Timing {
  return { offsetMs: 0, confirmed: false, meter: [{ t: Beats.ZERO, beatsPerBar: 4 }] };
}

function editorData(): ProjectEditorData {
  return { ignoredProblems: [], previewAudioDelayMs: 0, lastExport: null };
}

function rpeRoot(): RpeRootData {
  return { multiLineString: null, multiScale: null, chartTime: null, xybind: null, extra: [] };
}

function lineEditorData(): LineEditorData {
  return { tag: null, xyLinked: false, layerNames: [] };
}

function lineRpeData(_origin: 'editor' | 'import'): LineRpeData {
  // Editor lines and freshly lifted lines start from the same neutral record; the lifter overwrites the fields it
  // captured from the source (slots, null keys, extended presence, passthrough).
  return {
    bpmfactor: 1,
    anchor: [0.5, 0.5],
    isCoverRaw: null,
    skewControl: null,
    layerSlots: [],
    extended: 'object',
    notesKey: 'present',
    ignored: [],
    nullKeys: { extended: [], layers: [] },
    extra: [],
    extendedExtra: [],
    layerExtra: [],
  };
}

function noteControls(): NoteControls {
  return { pos: null, alpha: null, size: null, y: null };
}

function noteTemplate(): NoteTemplate {
  return { above: true, fake: false, speed: 1, yOffset: 0, size: 1, alpha: 255, visibleTime: null };
}

function rpeDefaultControl(_key: 'pos' | 'alpha' | 'size' | 'y'): ControlCurve {
  // RPE writes {x: 0, easing: 1, <key>: 1} and {x: 9999999, easing: 1, <key>: 1} for every control (F§12).
  return {
    points: [
      { x: 0, easing: 1, value: 1 },
      { x: RPE_DEFAULT_CONTROL_END_X, easing: 1, value: 1 },
    ],
  };
}

function leadInValue(prop: LineProp, layer: number, kind: LineKind, uiMover: boolean): number | null {
  if (layer > 0) return 0;
  switch (prop) {
    case 'alpha':
    case 'moveX':
    case 'moveY':
    case 'rotate':
    case 'speed':
    case 'incline':
      return 0;
    case 'paint':
      return -1;
    case 'scaleX':
      // Absent scaleX on a line.png line (normal or paint brush) is factor 1; a key value v draws v * 0.5 (F§9.1,
      // R§3.7), so 2 renders like the absent track. Texture, GIF, text and UI lines have no x0.5 rule.
      return !uiMover && (kind === 'normal' || kind === 'paint') ? 2 : 1;
    case 'scaleY':
      // Normal-line thickness: absent is 0.01 units, keyed is 0.0076 * v, so no key value matches the absent track.
      // Every other kind (paint brush included, R§3.7) uses scaleY as a plain factor: 1 renders like absent.
      return !uiMover && kind === 'normal' ? null : 1;
    case 'gif':
    case 'color':
    case 'text':
      // GIF lead-in auto-plays; colour and text are not numbers.
      return null;
  }
}

function emptyDoc(opts: {
  readonly name: string;
  readonly bpm: number;
  readonly offsetMs: number;
  readonly rpeId: string;
}): ChartDoc {
  const bpmId = FIRST_ID as BpmId;
  const point: BpmPoint = { id: bpmId, t: Beats.ZERO, bpm: opts.bpm };
  return {
    kind: 'phiedit-doc',
    version: 1,
    idSeq: FIRST_ID + 1,
    meta: { ...meta(), name: opts.name, rpeId: opts.rpeId },
    pkg: pkg(),
    timing: { ...timing(), offsetMs: opts.offsetMs },
    editor: editorData(),
    rpeRoot: rpeRoot(),
    bpm: new Map([[bpmId, point]]),
    lines: new Map(),
    lineOrder: [],
    tracks: new Map(),
    keys: new Map(),
    notes: new Map(),
    effects: new Map(),
    effectOrder: [],
    videos: new Map(),
    markers: new Map(),
    groups: new Map(),
    generators: new Map(),
    assets: new Map(),
  };
}

export const Defaults: ModelDefaultsApi = {
  emptyDoc,
  meta,
  pkg,
  timing,
  editorData,
  rpeRoot,
  lineEditorData,
  lineRpeData,
  noteControls,
  noteTemplate,
  rpeDefaultControl,
  leadInValue,
};

/**
 * Default song-select preview range for a song of `durationSec` (DESIGN.md / model contract): start at
 * min(chosen, max(0, duration - 16)), end 15 s later, clamped to duration - 0.5. Without a duration: [chosen, null].
 */
export function previewRangeFor(durationSec: number | null, chosenStart = 0): { start: number; end: number | null } {
  const chosen = Number.isFinite(chosenStart) && chosenStart > 0 ? chosenStart : 0;
  if (durationSec === null || !Number.isFinite(durationSec) || durationSec <= 0) return { start: chosen, end: null };
  const start = Math.min(chosen, Math.max(0, durationSec - 16));
  const limit = durationSec - 0.5;
  const end = Math.min(start + PREVIEW_LENGTH_SEC, limit);
  return end > start ? { start, end } : { start, end: null };
}
